import { getApi } from "../Services/apiService";

/**
 * The "Birthdays by Family Lines" report — GET
 * /report-management/birthdays-by-family-lines-report (rpt_ByLines).
 *
 * The endpoint does the work: every family member (in-laws included, the
 * deceased too — present_or_deceased 2)
 * with a birth date, grouped under their family line, each line's members
 * sorted by month then day of birth. No head_id comes back — the page finds
 * the head through the line's colour; the head's name rides on each member.
 *
 * The live payload differs from the Swagger doc (total_members / total_count /
 * birthdays, not total_member_count / count / members; dob is epoch ms), so
 * both spellings are read.
 *
 * Sample payload:
 *   { family_tree_id, total_members: 193,
 *     family_lines: [ { color_name: "Dk Blue", hex_code: "#0E1667",
 *       total_count: 11,
 *       birthdays: [ { id, name: "Sherry Gassaway", first_name, middle_name,
 *         last_name, suffix, special_name, dob: "-29116800000", month: 1,
 *         day: 29, generation_level: 4, head_first_name: "Mary M",
 *         head_last_name, head_suffix, head_special_name,
 *         is_spouse: true } ] } ] }
 */

const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const trimmed = (value) => (value == null ? "" : String(value).trim());

/** dob as epoch ms — the payload sends it as a ms string, the doc as ISO. */
function toBirthTime(dob) {
  const text = trimmed(dob);
  if (!text) return null;
  if (/^-?\d+$/.test(text)) return Number(text);
  const time = Date.parse(text);
  return Number.isFinite(time) ? time : null;
}

/** Month/day off the payload, else read off the dob (UTC, as the server stores it). */
function toBirthdayMember(member, line) {
  const birthTime = toBirthTime(member?.dob);
  const birthDate = birthTime == null ? null : new Date(birthTime);
  const isSpouse = member?.is_spouse === true || member?.is_spouse === "true";

  return {
    ...member,
    display_name: trimmed(member?.display_name || member?.name),
    dob: birthTime,
    month: toNumberOrNull(member?.month) ?? (birthDate ? birthDate.getUTCMonth() + 1 : null),
    day: toNumberOrNull(member?.day) ?? (birthDate ? birthDate.getUTCDate() : null),
    age: toNumberOrNull(member?.age),
    // The dashboard name format prints an in-law's last name; descendants go by first name.
    is_in_laws: member?.is_in_laws ?? isSpouse,
    color_name: trimmed(member?.color_name) || line.colorName,
    hex_code: trimmed(member?.hex_code) || line.hexCode,
  };
}

/** The line's head as its members name them: "Mary M", "Frank, Sr". */
function getHeadNameFromMembers(members) {
  const withHead = members.find((member) => trimmed(member?.head_first_name));
  if (!withHead) return "";
  const suffix = trimmed(withHead.head_suffix);
  const special = trimmed(withHead.head_special_name);
  return [
    trimmed(withHead.head_first_name) + (suffix ? `, ${suffix}` : ""),
    special ? `"${special}"` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

const EMPTY_REPORT = { totalCount: 0, lines: [] };

/**
 * Who a row is: its id, and its full name + birth date — the report can send an
 * in-law twice (once off the member table, once off the spouse's record) under
 * different ids, and two people never share both.
 */
function getMemberIdentityKeys(member) {
  const keys = [];
  const id = trimmed(member?.id ?? member?.member_id);
  if (id) keys.push(`id:${id}`);
  const fullName = [member?.first_name, member?.middle_name, member?.last_name, member?.suffix]
    .map(trimmed)
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (fullName && member?.dob != null) keys.push(`name:${fullName}|${member.dob}`);
  return keys;
}

/**
 * Each person once across the whole report. A person listed in two lines keeps
 * the line they were born into (the row that is not a spouse row), else the first.
 */
function dedupeLineMembers(lines) {
  const isSpouseRow = (member) => member?.is_spouse === true || member?.is_spouse === "true";
  const claims = new Map(); // identity key -> { slot, spouse }
  const dropped = new Set(); // "line:member" slots that lost to another row

  lines.forEach((line, lineIndex) =>
    line.members.forEach((member, memberIndex) => {
      const keys = getMemberIdentityKeys(member);
      const claim = { slot: `${lineIndex}:${memberIndex}`, spouse: isSpouseRow(member) };
      const existing = keys.map((key) => claims.get(key)).find(Boolean);
      if (existing && !(existing.spouse && !claim.spouse)) {
        dropped.add(claim.slot);
        return;
      }
      if (existing) {
        dropped.add(existing.slot);
        claims.forEach((value, key) => {
          if (value === existing) claims.set(key, claim);
        });
      }
      keys.forEach((key) => claims.set(key, claim));
    })
  );

  const deduped = lines.map((line, lineIndex) => {
    const members = line.members.filter(
      (_, memberIndex) => !dropped.has(`${lineIndex}:${memberIndex}`)
    );
    const removed = line.members.length - members.length;
    return removed ? { ...line, members, totalCount: Math.max(0, line.totalCount - removed) } : line;
  });
  return { lines: deduped, removed: dropped.size };
}

/**
 * The report: one entry per family line, in the report's own order, each with
 * its members in birthday (month/day) order.
 *
 * @param {string} familyTreeId
 */
export async function fetchBirthdaysByFamilyLines(familyTreeId) {
  if (!familyTreeId) return EMPTY_REPORT;

  const response = await getApi(
    `/report-management/birthdays-by-family-lines-report?family_tree_id=${encodeURIComponent(
      familyTreeId
    )}`
  );

  if (response?.statusCode !== 200 || !response?.data) return EMPTY_REPORT;

  const rawLines = Array.isArray(response.data.family_lines) ? response.data.family_lines : [];

  const parsedLines = rawLines
    .map((rawLine, index) => {
      const rawMembers = Array.isArray(rawLine?.birthdays)
        ? rawLine.birthdays
        : Array.isArray(rawLine?.members)
        ? rawLine.members
        : [];
      const line = {
        headName: trimmed(rawLine?.head_name) || getHeadNameFromMembers(rawMembers),
        colorName: trimmed(rawLine?.color_name),
        hexCode: trimmed(rawLine?.hex_code),
        order: index,
      };
      const members = rawMembers.map((member) => toBirthdayMember(member, line));
      return {
        ...line,
        totalCount:
          toNumberOrNull(rawLine?.total_count) ?? toNumberOrNull(rawLine?.count) ?? members.length,
        members,
      };
    });

  const { lines: dedupedLines, removed } = dedupeLineMembers(parsedLines);
  const lines = dedupedLines.filter((line) => line.members.length > 0);

  const reportTotal =
    toNumberOrNull(response.data.total_members) ?? toNumberOrNull(response.data.total_member_count);

  return {
    totalCount:
      reportTotal != null
        ? Math.max(0, reportTotal - removed)
        : lines.reduce((sum, line) => sum + line.totalCount, 0),
    lines,
  };
}
